// The messages between the server and the extension about where chats were
// started, kept free of the port and the hook so they can be tested on their
// own. The wire contract is the wavebox_infinity plan
// `docs/plans/active/brainbox-chat-origin.md`.

/**
 * The extension message for a `chat_index` push from the server, or null for
 * anything that is not one.
 * @param {*} data - `{ op: 'upsert', entry }` or `{ op: 'delete', id }`.
 * @returns {{type: 'chatUpserted', data: Object} | {type: 'chatDeleted', data: {id: string}} | null}
 */
export function chatIndexMessage (data) {
  if (data?.op === 'upsert' && typeof data.entry?.id === 'string') {
    return { type: 'chatUpserted', data: data.entry }
  }
  if (data?.op === 'delete' && typeof data.id === 'string') {
    return { type: 'chatDeleted', data: { id: data.id } }
  }
  return null
}

/**
 * The `open_chat` event for the extension's `openChat` push, or null when it
 * names neither a chat nor a filter. A filter keeps only its non-empty string
 * `space`, `group` and `app`; the server picks the innermost and validates it.
 * @param {*} data - `{ chatId }` or `{ filter: { space?, group?, app? } }`.
 * @returns {{chatId: string} | {filter: {space?: string, group?: string, app?: string}} | null}
 */
export function openChatEvent (data) {
  const chatId = data?.chatId
  if (typeof chatId === 'string' && chatId !== '') {
    return { chatId }
  }

  const filter = {}
  for (const kind of ['space', 'group', 'app']) {
    const id = data?.filter?.[kind]
    if (typeof id === 'string' && id !== '') {
      filter[kind] = id
    }
  }
  return Object.keys(filter).length > 0 ? { filter } : null
}

/**
 * The `new_chat_context_received` event answering the server's
 * `request_new_chat_context`: the fresh context's tab, selection and origin,
 * and the `chat_id` of the chat that asked, echoed back so the server stamps
 * that chat and not whichever is open when the answer arrives. `context` is
 * null when the extension could not be asked.
 * @param {*} request - The server's `{ chat_id }`.
 * @param {*} context - The extension's `getInitialContext` reply, or null.
 * @returns {{chat_id: string | null, activeTab: *, selection: *, origin: *}}
 */
export function newChatContextReply (request, context) {
  const chatId = request?.chat_id
  return {
    chat_id: typeof chatId === 'string' && chatId !== '' ? chatId : null,
    activeTab: context?.activeTab || null,
    selection: context?.selection || null,
    origin: context?.origin || null,
    // vfs1 clients only; the server keeps today's tab attachment without it
    scopeCandidates: context?.scopeCandidates || null
  }
}
